import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import DocumentChatShell from '../shells/DocumentChatShell';
import AssistantBotRow from '../shells/AssistantBotRow';
import { useDocumentChatNavigation } from '../shells/useDocumentChatNavigation';
import ChatSaveErrorBanner from '../components/ChatSaveErrorBanner';
import ContinueButtonBar from '../components/ContinueButtonBar';
import PdfPreviewActionButton from '../components/PdfPreviewActionButton';
import DocumentChatLayout from '../layout/DocumentChatLayout';
import { interpolateI18n } from '../utils/interpolateI18n';
import { useChatThreadScroll } from '../hooks/useChatThreadScroll';
import { useStaggeredPreviewCta } from '../hooks/useStaggeredPreviewCta';
import PreEmploymentEducationChatComposer, {
  PreEmploymentEducationDeclinedComposer,
} from './PreEmploymentEducationChatComposer';
import PreEmploymentEducationChatThread from './PreEmploymentEducationChatThread';
import { usePreEmploymentEducationFlow } from './usePreEmploymentEducationFlow';

const FIRST_ASSISTANT_DELAY_MS = 1000;
const FOLLOW_ASSISTANT_GAP_MS = 650;

export interface PreEmploymentEducationChatFlowProps {
  documentTitle: string;
  currentEmail?: string | null;
  invitationId?: string | null;
  onBack: () => void;
  onOpenPdfPreview?: () => void;
  onCompleteContinue?: () => void | Promise<void>;
  onDeclinedContinue?: () => void | Promise<void>;
}

const PreEmploymentEducationChatFlow: React.FC<PreEmploymentEducationChatFlowProps> = ({
  documentTitle,
  currentEmail,
  invitationId,
  onBack,
  onOpenPdfPreview,
  onCompleteContinue,
  onDeclinedContinue,
}) => {
  const { t } = useLanguage();
  const { exitToChecklist } = useDocumentChatNavigation(onBack);
  const flow = usePreEmploymentEducationFlow(currentEmail, invitationId, t);

  const [revealPhase, setRevealPhase] = useState(0);
  const threadEndRef = useRef<HTMLDivElement | null>(null);

  const completeThankYou = useMemo(
    () => t('submitDocuments.preEmploymentEducation.completeThankYou'),
    [t]
  );

  const previewIntro = useMemo(
    () => interpolateI18n(t('submitDocuments.preEmploymentEducation.previewIntro'), { documentTitle }),
    [t, documentTitle]
  );

  const atComplete = flow.isComplete;
  const { showIntro: showCompleteMessage, showCta: showCompleteCta } = useStaggeredPreviewCta(atComplete);

  useEffect(() => {
    if (flow.isLoading) return;
    setRevealPhase(0);
    const t1 = window.setTimeout(() => setRevealPhase(1), FIRST_ASSISTANT_DELAY_MS);
    const t2 = window.setTimeout(
      () => setRevealPhase(2),
      FIRST_ASSISTANT_DELAY_MS + FOLLOW_ASSISTANT_GAP_MS
    );
    const t3 = window.setTimeout(
      () => setRevealPhase(3),
      FIRST_ASSISTANT_DELAY_MS + FOLLOW_ASSISTANT_GAP_MS * 2
    );
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.clearTimeout(t3);
    };
  }, [documentTitle, flow.isLoading]);

  useChatThreadScroll(threadEndRef, [
    revealPhase,
    flow.committed.length,
    flow.isComplete,
    flow.materialsDeclined,
    flow.editingCommitIndex,
    showCompleteMessage,
  ]);

  const canShowThread = revealPhase >= 3 && !flow.isLoading;

  return (
    <DocumentChatShell onBack={exitToChecklist} cancelLabel={t('submitDocuments.chat.cancelProcedure')}>
      <DocumentChatLayout
        thread={
          <>
            {revealPhase >= 1 && (
              <AssistantBotRow animateIn>{t('submitDocuments.preEmploymentEducation.intro1')}</AssistantBotRow>
            )}
            {revealPhase >= 2 && (
              <AssistantBotRow animateIn>{t('submitDocuments.preEmploymentEducation.intro2')}</AssistantBotRow>
            )}
            {flow.isLoading ? (
              revealPhase >= 2 && (
                <AssistantBotRow animateIn>{t('submitDocuments.preEmploymentEducation.loading')}</AssistantBotRow>
              )
            ) : canShowThread ? (
              <PreEmploymentEducationChatThread flow={flow} />
            ) : null}
            {atComplete && showCompleteMessage && (
              <AssistantBotRow animateIn>{completeThankYou}</AssistantBotRow>
            )}
            {atComplete && showCompleteCta && onOpenPdfPreview && (
              <AssistantBotRow animateIn>{previewIntro}</AssistantBotRow>
            )}
            <div ref={threadEndRef} aria-hidden />
            <div ref={flow.threadEndRef} aria-hidden />
          </>
        }
        composer={
          <>
            <ChatSaveErrorBanner message={flow.saveError} />
            {flow.materialsDeclined && onDeclinedContinue && canShowThread && (
              <PreEmploymentEducationDeclinedComposer onContinue={() => void onDeclinedContinue()} />
            )}
            {atComplete && showCompleteCta && onOpenPdfPreview && (
              <PdfPreviewActionButton
                label={t('submitDocuments.preEmploymentEducation.previewPdf')}
                onClick={() => {
                  void (async () => {
                    const ok = await flow.handleFinalComplete();
                    if (ok) onOpenPdfPreview();
                  })();
                }}
              />
            )}
            {atComplete && showCompleteCta && !onOpenPdfPreview && onCompleteContinue && (
              <ContinueButtonBar
                onContinue={() => {
                  void (async () => {
                    const ok = await flow.handleFinalComplete();
                    if (ok) onCompleteContinue();
                  })();
                }}
              />
            )}
            {!flow.isComplete && !flow.materialsDeclined && !flow.isLoading && canShowThread ? (
              <PreEmploymentEducationChatComposer flow={flow} />
            ) : null}
          </>
        }
      />
    </DocumentChatShell>
  );
};

export default PreEmploymentEducationChatFlow;
